// src/Component/Workshop/CaseDetail/CaseProgressChecklist.jsx

import { useEffect, useRef } from "react";
import { formatDate } from "../UI";
import { CHECKLIST_STEPS } from "./config/workflowConfig";

export function CaseProgressChecklist({ caseInfo, detail }) {
  const scrollRef = useRef(null);

  const rawStatuses = [
    ...(detail?.timeline || []).map(t => t.eventType ?? t.status),
    caseInfo.currentStatus,
  ].filter(Boolean);

  const doneStatuses = new Set(rawStatuses);
  const maxDoneIndex = Math.max(
    ...CHECKLIST_STEPS.map((s, i) => doneStatuses.has(s.id) ? i : -1)
  );
  const inferredDoneStatuses = new Set(
    CHECKLIST_STEPS.slice(0, maxDoneIndex + 1).map(s => s.id)
  );

  const currentIdx = CHECKLIST_STEPS.findIndex(s => s.id === caseInfo.currentStatus);
  const safeIdx    = currentIdx === -1 ? 0 : currentIdx;
  const doneCount  = CHECKLIST_STEPS.filter(s => inferredDoneStatuses.has(s.id)).length;


  // Auto-scroll active step to center
  useEffect(() => {
    const wrap = scrollRef.current;
    const el   = wrap?.querySelector(`[data-step="${safeIdx}"]`);
    if (!wrap || !el) return;
    const left = el.offsetLeft - wrap.offsetWidth / 2 + el.offsetWidth / 2;
    wrap.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
  }, [safeIdx]);
  
  return (
    <div className="wd-card" style={{ padding: "14px 20px" }}>

      {/* Header — same as before */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-t1)" }}>Case Progress</span>
        <span style={{ fontSize: 11, color: "var(--wd-t3)" }}>{doneCount} / {CHECKLIST_STEPS.length} completed</span>
      </div>

      {/* Scrollable 16-status track */}
      <div
        ref={scrollRef}
        style={{
          overflowX: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: "var(--wd-border-md) transparent",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", minWidth: "max-content", padding: "4px 2px 8px" }}>
          {CHECKLIST_STEPS.map((s, i) => {

            const isDone    = inferredDoneStatuses.has(s.id);
            const isActive  = s.id === caseInfo.currentStatus;
            const isSkipped = s.optional && !isDone && !isActive;

            const state = isDone ? "done" : isActive ? "active" : isSkipped ? "skipped" : "pending";

            const styles = {
              done:    { dot: "var(--wd-green)",     border: "var(--wd-green)",     label: "var(--wd-t1)",   sub: "var(--wd-t3)",        subText: "✓",           opacity: 1    },
              active:  { dot: "var(--wd-blue)",      border: "var(--wd-blue)",      label: "var(--wd-blue)", sub: "var(--wd-blue)",       subText: "In progress", opacity: 1    },
              skipped: { dot: "transparent",         border: "var(--wd-border-md)", label: "var(--wd-t3)",   sub: "var(--wd-border-md)",  subText: "Skipped",     opacity: 0.4  },
              pending: { dot: "var(--wd-hover)",     border: "var(--wd-border-md)", label: "var(--wd-t3)",   sub: "var(--wd-orange)",     subText: "Pending",     opacity: 1    },
            }[state];

              return (
              <div key={s.id} data-step={i}
                style={{ display: "flex", alignItems: "center", flex: i < CHECKLIST_STEPS.length - 1 ? 1 : 0 }}
              >
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, opacity: styles.opacity }}>

                  {/* Dot */}
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: styles.dot,
                    border: `2px solid ${styles.border}`,
                    boxShadow: isActive ? "0 0 0 4px rgba(59,130,246,.15)" : "none",
                  }}>
                    {isDone ? (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    ) : (
                      <div style={{
                        width: 8, height: 8, borderRadius: "50%",
                        background: isActive ? "#fff" : "var(--wd-border-md)",
                      }}/>
                    )}
                  </div>

                  {/* Label */}
                  <div style={{ textAlign: "center", minWidth: 80 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", lineHeight: 1.3, color: styles.label }}>
                      {s.label}
                      {s.optional && (
                        <span style={{ fontSize: 8, marginLeft: 3, opacity: .6 }}>(opt)</span>
                      )}
                    </div>
                    <div style={{ fontSize: 9, marginTop: 2, color: styles.sub }}>{styles.subText}</div>
                  </div>
                </div>

                {/* Connector */}
                {i < CHECKLIST_STEPS.length - 1 && (
                  <div style={{
                    flex: 1, height: 2, minWidth: 20,
                    background: isDone ? "var(--wd-green)" : "var(--wd-border-md)",
                    margin: "0 6px", marginBottom: 28,
                    opacity: isSkipped ? 0.25 : 1,
                  }}/>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}